/**
 * Per-service page content and SEO.
 *
 * WHY THESE PAGES EXIST
 * ---------------------
 * Every link to every one of the eight services used to resolve to
 * `/#services`. A fragment is not a page: it cannot carry its own title,
 * description, H1 or canonical URL, so one landing page was left competing
 * for eight different sets of queries at once and none of them could be
 * answered specifically. Each service now has a real URL under `/services/`,
 * prerendered and in the sitemap like any other page.
 *
 * TWO KINDS OF ENTRY
 * ------------------
 * Most entries are keyed by an `id` in configServices.js, which stays the
 * source of truth for the title, short description and tags shown in the
 * landing grid — they are reused here rather than restated, so the two cannot
 * disagree. Such an entry adds only what a standalone page needs: a slug,
 * meta copy and body prose.
 *
 * The rest are standalone: a real offering that has a page and a menu entry
 * but is deliberately absent from the landing grid, so it carries its own
 * `title`, `description` and `tags`. QA, product management and maintenance
 * are these. They were menu items pointing at the #services anchor, which
 * meant three searched-for services with nowhere to land; they stay out of
 * the grid because that grid's eight cards and their AI-first order are the
 * positioning, and commodity services dilute it.
 *
 * `resolveServicePage` below hides the difference from every consumer.
 *
 * COPY STATUS — read before publishing
 * ------------------------------------
 * The prose below describes what each discipline involves, written from the
 * descriptions and tags already on the site. It deliberately contains no
 * client name, project outcome or metric, because inventing those would be
 * both dishonest and the fastest way to lose the trust these pages exist to
 * earn. Two consequences:
 *
 *   1. Review the wording. It is accurate as far as it goes, but it is not
 *      written by the people who did the work.
 *   2. Each page wants one real example with a measurable result before it
 *      will compete for anything contested. That is the single highest-value
 *      addition and it needs you, not a generator. See docs/TODO-REAL-DATA.md.
 *
 * Near-duplicate service pages are worse than no service pages, so if a
 * service is ever added here, give it its own argument rather than a reworded
 * copy of its neighbour's.
 *
 * FIELDS
 * ------
 *   slug, metaTitle, metaDescription   URL and search-result copy
 *   heading, intro                     the hero
 *   valueProps  (3)                    what the client gets
 *   body        (2 paragraphs)         how we approach it
 *   challenges  (3)                    the problems it solves
 *   faqs        (4+)                   questions, in roughly the words
 *                                      people search for them in
 *   tags                               what the work includes
 *   caseStudyIds                       ids from configPortfolio
 *   reviewId                           an id from configReviews
 *   title, description, tags           standalone pages only
 *
 * THE TWO EMPTY SLOTS
 * -------------------
 * `caseStudyIds` and `reviewId` are the proof on these pages, and they are
 * the two things that cannot be written: a case study has to be a project
 * that really demonstrates the service, and attaching a client's words to a
 * service page claims they endorsed that service specifically.
 *
 * Only `marketplaces` is populated, from the four portfolio projects whose
 * own taglines describe marketplaces. Every other page renders neither
 * section until someone who knows the work chooses. Filling them is the
 * highest-value edit available to these pages — comparable agency pages lead
 * with proof, and this is where ours goes.
 *
 * There is deliberately no statistics block ("18+ years, 400+ projects") and
 * no client logo wall, because every number would have to be invented. See
 * docs/TODO-REAL-DATA.md.
 */
import { configServices } from './configServices';
import sharetribeMark from '../assets/sharetribe-mark.png';

export const configServicePages = {
  aiAgents: {
    slug: 'ai-agent-development',
    metaTitle: 'AI & Agent Development Services',
    metaDescription:
      'Assistants and agents that do real work inside your product — grounded in your own data, with evaluations and guardrails built in from the first week.',
    heading: 'AI and agent development',
    intro:
      'We build AI features that sit inside a product and carry real responsibility: answering from your own documents, drafting work a person then approves, or taking a multi-step task from request to finished result.',
    body: [
      'The model is rarely the hard part. What decides whether an AI feature survives contact with users is everything around it — how context is retrieved and ranked, what the system is allowed to do, what happens when it is wrong, and whether you can measure any of it. So that is what we build first: retrieval over your data, explicit tool and action boundaries, logging of every prompt and response, and an evaluation set that can tell you whether a change made the system better or merely different.',
      'It also means being straight about where AI does not belong. A deterministic rule, a search index or an ordinary form is often the better answer, and saying so in week one is far cheaper than discovering it after launch.',
    ],

    valueProps: [
      {
        title: 'Answers grounded in your data',
        body:
          'Retrieval over your own documents, tickets and records, so the system answers from what your business actually knows rather than from what a model happens to remember.',
      },
      {
        title: 'Actions with explicit boundaries',
        body:
          'Agents that call your tools and APIs inside limits you set, with every call logged, so an automated step can be audited and undone rather than merely trusted.',
      },
      {
        title: 'Evaluation from the first week',
        body:
          'A scored test set built alongside the feature, so you can tell whether a prompt, model or retrieval change improved the system or only changed it.',
      },
    ],

    challenges: [
      {
        title: 'A demo that does not survive real users',
        body:
          'Prompt-only prototypes impress in a meeting and then meet the long tail of real questions. What closes the gap is retrieval, guardrails and evaluation, not a longer prompt.',
      },
      {
        title: 'No way to tell whether it is any good',
        body:
          'Without an evaluation set, every change is a guess and every regression is discovered by a customer. Quality becomes an opinion rather than a number.',
      },
      {
        title: 'Inference cost that scales badly',
        body:
          'Spend climbs faster than usage when every step calls the largest model. Caching, routing and smaller models for the simple parts keep it proportional to value.',
      },
    ],

    faqs: [
      {
        question: 'What can an AI feature realistically do inside our product?',
        answer:
          'Reliably: answer questions from your own content with citations, draft work a person reviews, extract structure from messy input, and carry out multi-step tasks against your own APIs. Less reliably: anything where being confidently wrong is expensive and no human checks the output. We design for the first set and put a review step wherever the second is unavoidable.',
      },
      {
        question: 'Do we need to train or fine-tune our own model?',
        answer:
          'Almost never at the start. Retrieval over your data plus a capable general model answers most product needs, and it is far cheaper to change your mind about. Fine-tuning earns its place for consistent formatting, a narrow classification task, or cost reduction once traffic justifies it — after an evaluation set exists to prove it helped.',
      },
      {
        question: 'How do you stop it inventing answers?',
        answer:
          'Four things together: ground every answer in retrieved passages, cite them so a reader can check, give the system an explicit way to say it does not know, and score all of that on a test set before release. Hallucination cannot be driven to zero, so the design question is what happens when it occurs, not whether it will.',
      },
      {
        question: 'How do we start without committing to a large build?',
        answer:
          'With a scoped pilot against one real use case, including the evaluation set, so the result is evidence rather than a demo. Our AI pilot engagement runs four to six weeks and ends with a working feature, its scores, and an honest recommendation — which is sometimes that the problem does not need AI.',
      },
    ],

    caseStudyIds: [],
  },

  dataMl: {
    slug: 'data-ml-engineering',
    metaTitle: 'Data & ML Engineering Services',
    metaDescription:
      'The pipelines, search and feature work AI depends on, so a model reasons over current, trustworthy data instead of a stale export.',
    heading: 'Data and ML engineering',
    intro:
      'An AI feature is only as good as the data reaching it. We build the ingestion, storage and retrieval layers that keep a model working from information that is current and that you can vouch for.',
    body: [
      'This work is unglamorous and decisive: pipelines that move data on a schedule and say so loudly when they fail, schemas that survive the next requirement, and retrieval that combines vector and keyword search because neither alone returns the right passage often enough. Getting it wrong shows up later as an assistant that confidently quotes last quarter.',
      'Where conventional machine learning is the better tool than a language model — ranking, classification, forecasting — we train and serve those too, with the monitoring needed to notice when a model has quietly drifted away from the world it was trained on.',
    ],

    valueProps: [
      {
        title: 'Pipelines that announce their own failure',
        body:
          'Scheduled ingestion with monitoring and alerting attached, so stale data is something you are told about rather than something a user finds.',
      },
      {
        title: 'Retrieval that returns the right passage',
        body:
          'Vector and keyword search tuned together and measured on real queries, because neither alone finds what people actually ask for often enough.',
      },
      {
        title: 'Models you can keep watching',
        body:
          'Training and serving with the monitoring needed to notice drift, so a model that has quietly stopped matching the world gets caught early.',
      },
    ],

    challenges: [
      {
        title: 'A live feature answering from stale data',
        body:
          'An assistant that confidently quotes last quarter is worse than no assistant. Freshness is a pipeline problem, not a prompt problem.',
      },
      {
        title: 'Search that misses the obvious',
        body:
          'Pure vector search fails on exact terms, part numbers and names; pure keyword search fails on meaning. Most real retrieval needs both, scored against real queries.',
      },
      {
        title: 'Nobody can answer where a number came from',
        body:
          'When metrics are assembled by hand from exports, two reports disagree and neither can be traced. Lineage has to be built in, not reconstructed afterwards.',
      },
    ],

    faqs: [
      {
        question: 'What does an AI feature actually need from our data?',
        answer:
          'Less than people expect, and in better shape than they expect. A narrow, current, well-described slice beats a large messy one: the documents that answer real questions, chunked sensibly, with metadata for filtering and permissions, refreshed on a schedule. Most projects spend more time on access and freshness than on modelling.',
      },
      {
        question: 'Do we need a warehouse before we can do anything?',
        answer:
          'No. A warehouse is worth building when several teams need to query the same history; it is overkill for one product feature. We start from the question you need answered and build the smallest pipeline that answers it reliably, then grow that into shared infrastructure if demand appears.',
      },
      {
        question: 'Vector search or keyword search?',
        answer:
          'Both, almost always, combined and re-ranked. Vector search handles paraphrase and meaning, keyword search handles exact identifiers, and the hybrid of the two is what makes retrieval feel like it understands the question. We measure the combination on your own queries rather than trusting a default.',
      },
      {
        question: 'How do you handle personal or restricted data?',
        answer:
          'By keeping permission checks in the retrieval layer rather than hoping the model behaves: a query only ever sees passages the requesting user is entitled to. Beyond that, the usual discipline — minimise what is copied, redact what is not needed, record what is stored where, and keep prompts and responses out of logs that are not secured.',
      },
    ],

    caseStudyIds: [],
  },

  customSoftwareDevelopment: {
    slug: 'custom-software-development',
    metaTitle: 'Custom Software Development',
    metaDescription:
      'Tailored systems built around how your business actually operates — from a working prototype to a product your own team can run and extend.',
    heading: 'Custom software development',
    intro:
      'Software worth building usually exists because the off-the-shelf option forces your business to work the way the tool works. We start from the process you actually run and build around it.',
    body: [
      "That begins with modelling the real workflow, including the parts that live in spreadsheets, inboxes and people's heads. A prototype follows quickly, because an argument about a screen someone can click is far more productive than an argument about a document. From there it becomes a system with the unexciting essentials in place: tests, migrations, audit trails, and an administrative view for the people who have to operate it daily.",
      "We also build it to be handed over rather than depended on. Readable code, decisions written down where the next person will look for them, and a deployment anyone on your side can run matter more across a product's life than any individual feature — they are what stops the second year costing more than the first.",
    ],

    valueProps: [
      {
        title: 'Built around your actual process',
        body:
          'We model the workflow you really run, including the parts living in spreadsheets and inboxes, so the software fits the business rather than the reverse.',
      },
      {
        title: 'Something clickable early',
        body:
          'A working prototype arrives while changing direction is still cheap, because an argument about a screen someone can use is far more productive than one about a document.',
      },
      {
        title: 'Handover assumed from the start',
        body:
          'Readable code, decisions written down, and a deployment your own team can run — the things that decide what year two costs.',
      },
    ],

    challenges: [
      {
        title: 'The tool forces the business to bend',
        body:
          "Off-the-shelf software encodes someone else's process. When the workarounds start costing more than a build, custom is the cheaper answer.",
      },
      {
        title: 'Spreadsheets as load-bearing infrastructure',
        body:
          'Critical operations running on a shared sheet with no history, no validation and no permissions is a risk that grows quietly until something is lost.',
      },
      {
        title: 'Only one person understands it',
        body:
          'A system nobody else can change is a system that cannot be improved, and an availability risk the day that person is unavailable.',
      },
    ],

    faqs: [
      {
        question: 'Should we build or buy?',
        answer:
          'Buy whenever a product does the job and your process can reasonably adapt — payments, email, analytics and auth are rarely worth building. Build where the process is the advantage, where the data is yours, or where integration between several bought tools is the actual product. We will tell you when the answer is buy, which is sometimes the end of the conversation.',
      },
      {
        question: 'How do you scope something that is not fully defined yet?',
        answer:
          'By splitting it: a short, fixed piece of discovery that produces a prototype and a written plan, then delivery in increments against that plan. Fixed-price for the whole of an undefined system is a fiction that gets paid for later in change requests, so we price what is known and keep re-planning cheap.',
      },
      {
        question: 'Who owns the code and the infrastructure?',
        answer:
          'You do, from the first commit — in your repository, your cloud accounts, your domains. We work in your accounts rather than ours precisely so that the handover is a non-event rather than a migration.',
      },
      {
        question: 'What happens after launch?',
        answer:
          'Whatever you need: a maintenance arrangement with us, a handover to your own team with documentation and pairing, or a period of support while you hire. The code is written on the assumption that someone else will maintain it, which is what makes any of those options real.',
      },
    ],

    caseStudyIds: [],
  },

  marketplaces: {
    slug: 'marketplace-development',
    metaTitle: 'Marketplace Development Company',
    metaDescription:
      'Marketplace specialists: we design and build two-sided marketplaces and platforms, with payments, payouts, search and trust systems that hold up at volume.',
    heading: 'Marketplace development, our specialism',
    badge: { label: 'Our specialism' },
    intro:
      'Marketplaces are what we are best at. A marketplace is two products in one: the supply side and the demand side, each useless without the other. We build both, together with the money movement and trust mechanics that decide whether either side comes back.',
    body: [
      'The interesting problems are rarely the listing pages. They are payouts and split payments, refunds and chargebacks, identity and verification, the matching and search that make a sparse catalogue feel full, and the moderation tools someone needs when a transaction goes wrong at 9pm. We build these as first-class parts of the product rather than as things to add once volume arrives, because by then they are load-bearing.',
      'We have shipped marketplaces for lighting design experts, sports and wellness bookings, childcare discovery, housing for educators and resale fashion, so the patterns below are ones we have built more than once rather than ones we have read about. That repetition is the point of hiring a specialist: the decisions that sink a first marketplace are mostly the same few, and we know where they hide.',
      'We are also comfortable choosing the platform. Where a marketplace platform gets you to market sooner, we will say so and build on it. Where its model fights your business, we build the parts that need to be yours and integrate the rest. You get an honest recommendation, not whichever option we happen to sell.',
    ],

    /*
     * An in-content link to the hidden Sharetribe page. Navigation is
     * deliberately not used for it; a contextual link is what lets the page
     * inherit this page's standing. `id` is a page id, `text` the anchor.
     */
    bodyLink: {
      id: 'sharetribe',
      lead: 'Already building on a marketplace platform? We also offer ',
      text: 'Sharetribe development and customization',
      trail: ', from first setup to taking over an existing marketplace.',
    },

    valueProps: [
      {
        title: 'Money movement that holds up',
        body:
          'Split payments, payouts, refunds and chargebacks built as first-class parts of the product, because by the time volume arrives they are load-bearing.',
      },
      {
        title: 'Trust and safety tooling',
        body:
          'Verification, reviews, reporting and the moderation screens someone needs when a transaction goes wrong outside office hours.',
      },
      {
        title: 'Matching that makes a thin catalogue feel full',
        body:
          'Search, filtering and ranking tuned for the early days, when supply is sparse and an empty result page costs you a buyer permanently.',
      },
    ],

    extraSections: [
      {
        id: 'models',
        heading: 'Marketplace models we build',
        intro:
          'The model decides the product. A rental calendar, a service quote and a resale listing look similar on a slide and share almost no logic underneath.',
        items: [
          {
            title: 'Booking and rental marketplaces',
            body:
              'Availability, calendars, deposits and cancellation rules, for experts, spaces, equipment and anything bought by the hour or the night.',
          },
          {
            title: 'Service and expert marketplaces',
            body:
              'Profiles, verification, enquiries and quotes, with the matching that turns a brief into the right three providers.',
          },
          {
            title: 'Product and resale marketplaces',
            body:
              'Listings, inventory, shipping and returns, including the condition grading and authenticity checks that preloved goods need.',
          },
          {
            title: 'B2B and procurement platforms',
            body:
              'Account hierarchies, approvals, invoicing and the integrations with the systems a buyer already runs.',
          },
          {
            title: 'Discovery and lead-generation platforms',
            body:
              'Directories where the transaction happens off-platform, and the product has to prove its value through enquiries and verified supply.',
          },
          {
            title: 'Vertical communities with commerce',
            body:
              'A membership or content product that adds payments, listings and payouts once the audience is there.',
          },
        ],
      },
      {
        id: 'sides',
        heading: 'Built for all three sides of the platform',
        intro:
          'Most marketplaces are designed for buyers and patched for everyone else. We design for the three people who decide whether it works.',
        items: [
          {
            title: 'For the supply side',
            body:
              'Onboarding, listing creation, calendars and inventory, and a dashboard that shows earnings, payouts and what to do next, so sellers stay.',
          },
          {
            title: 'For the demand side',
            body:
              'Search, filters and ranking, a checkout that does not lose people, messaging, reviews and the notifications that bring a buyer back.',
          },
          {
            title: 'For the operator',
            body:
              'Admin and moderation tools, dispute and refund handling, fee and commission control, and the reporting that tells you where the marketplace is leaking.',
          },
        ],
      },
      {
        id: 'approach',
        heading: 'Custom build, platform or both',
        intro:
          'The right foundation depends on how different your model is, how fast you need to launch and who will run it afterwards.',
        items: [
          {
            title: 'Build it custom',
            body:
              'When the matching, pricing or payout logic is the business. You own the code and the model, and nothing fights you as you grow.',
          },
          {
            title: 'Launch on a platform',
            body:
              'When a conventional marketplace model fits and speed matters. We set it up and extend it where your product needs to differ.',
          },
          {
            title: 'Start on a platform, replace what hurts',
            body:
              'Launch quickly, then rebuild the specific parts that become the constraint, instead of a risky rewrite or a decision made too early.',
          },
        ],
      },
    ],

    challenges: [
      {
        title: 'The cold start problem',
        body:
          'Neither side joins without the other. The product has to be useful to the first supplier with no buyers, and to the first buyer with little choice.',
      },
      {
        title: 'Disputes arriving faster than tooling',
        body:
          'Refunds, failed payouts and bad actors are operations problems before they are engineering problems, and support cannot run them from the database.',
      },
      {
        title: 'Supply that churns quietly',
        body:
          'Sellers leave when payouts are slow, listings are invisible or the dashboard tells them nothing. Retention is a product surface, not a marketing one.',
      },
      {
        title: 'Leakage off the platform',
        body:
          'Once two people have met, the fee is the only reason to stay. The product has to keep earning it through payments protection, reviews and repeat-booking tools.',
      },
      {
        title: 'Search that cannot find what exists',
        body:
          'Filters built for a hundred listings break at ten thousand. Relevance, facets and availability need designing before the catalogue grows, not after.',
      },
      {
        title: 'A build that cannot change pricing',
        body:
          'Commission rules, tiers and promotions change monthly. If each change is a deployment, the business learns slowly.',
      },
    ],

    faqs: [
      {
        question: 'How do payments and payouts actually work?',
        answer:
          'Usually Stripe Connect: buyers pay the platform, funds are held or split, and sellers are paid out on a schedule after onboarding and identity checks. The engineering that matters is the unhappy path — a payout that fails, a refund after a payout, a seller who never completes verification — and that is where most of the build time goes.',
      },
      {
        question: 'How do you deal with the cold start?',
        answer:
          'By not pretending the marketplace is one. Early on the product is concierge: supply is recruited by hand, matching can be manual behind the scenes, and the software focuses on making the few transactions that exist excellent. We build for that phase deliberately instead of launching a two-sided product to an empty room.',
      },
      {
        question: 'What about KYC, tax and compliance?',
        answer:
          'Identity and bank verification come with the payment provider, and we build the flows around them. Tax treatment, marketplace liability and whether you are the merchant of record are decisions for your accountant and lawyer — we implement what they decide and make sure the data they will need is recorded from the start.',
      },
      {
        question: 'How long does it take to build a marketplace?',
        answer:
          'A focused first version, with one clear transaction flow, payments and the operator tools needed to run it, is typically a matter of months rather than weeks. The honest answer comes from scope: we start with a short discovery to decide what the first version must do and what it can wait for, then give a timeline and a fixed-price milestone plan.',
      },
      {
        question: 'Can you build an MVP first and grow it?',
        answer:
          'Yes, and we usually recommend it. The first version proves that people will transact, using the smallest set of features that can do so safely. It is built so that search, payments and the data model can grow without being thrown away.',
      },
      {
        question: 'Which technology do you build marketplaces with?',
        answer:
          'Mostly React and Node.js, with Stripe Connect for payments, and a search layer chosen to fit the catalogue. We choose the stack for your team and your model, not ours, and we write it to be taken over by whoever runs it next.',
      },
      {
        question: 'Do you also build mobile apps for marketplaces?',
        answer:
          'Yes. We build iOS and Android apps from one React Native codebase and share the business logic and API with the web product, so the two do not drift apart. Mobile is usually phase two, once the web flow has proven that people will transact.',
      },
      {
        question: 'Can you take over or improve a marketplace that already exists?',
        answer:
          'Yes. We start with an audit of the code, the payment flows and the operational tooling, and then agree a plan to stabilise it, add what is missing and retire what is not working. This includes marketplaces built on a platform.',
      },
    ],

    tags: [
      'Marketplace strategy & MVP scoping',
      'Two-sided product design',
      'Stripe Connect payments & payouts',
      'Search, filters & matching',
      'Booking & availability',
      'Reviews, trust & verification',
      'Admin & moderation tools',
      'Marketplace migrations & audits',
    ],

    caseStudyIds: ['dialux', 'probuddy', 'truzeau', 'thecarelist'],
  },

  /*
   * HIDDEN PAGE — read before editing.
   *
   * `hidden: true` keeps this page out of every list that is built from the
   * registry (the services hub, the related-services block, the homepage
   * ItemList, the nav helpers), but it still gets a route, prerendered HTML,
   * canonical, structured data and a sitemap line. It is for people who
   * search for it by name. Do not link to it from navigation or the footer.
   *
   * `artId` borrows another service's mark and illustration.
   */
  sharetribe: {
    hidden: true,
    artId: 'marketplaces',
    graphicId: 'sharetribe',
    slug: 'sharetribe-development',
    metaTitle: 'Sharetribe Developers & Customization Experts',
    metaDescription:
      'Sharetribe experts for hire: marketplace setup, custom Sharetribe development, Web Template customization, integrations, upgrades and migrations.',
    heading: 'Sharetribe development and customization',
    badge: { label: 'Sharetribe experts', logo: sharetribeMark, logoAlt: 'Sharetribe' },
    title: 'Sharetribe development',
    description:
      'Sharetribe marketplace setup and customization: Web Template changes, custom transaction flows, integrations, upgrades and migrations.',
    intro:
      'We set up, customize and extend Sharetribe marketplaces. Whether you are launching on Sharetribe for the first time, outgrowing the template you started with or need a senior developer to take over an existing build, we do the work end to end.',
    body: [
      'Sharetribe is the fastest way to a working marketplace, and for many businesses it is the right foundation for years. The work is in making it yours: a design that does not look like every other template, a transaction flow that matches how you really sell, payments and fees that fit your model, and the integrations that connect it to the rest of your business.',
      'Marketplaces are what we specialise in, and we have built and extended Sharetribe marketplaces for clients with very different models. We know where the platform helps, where its assumptions get in the way, and how to work with them rather than around them, so your customizations stay maintainable when Sharetribe updates.',
      'We are an independent development agency. We are not part of Sharetribe, and we will tell you plainly if your model would be better served by a different approach, or if what you want is simpler than you expect and needs less customization than you had budgeted for.',
    ],

    valueProps: [
      {
        title: 'Set up properly the first time',
        body:
          'Marketplace configuration, listing types, fees, emails and payments set up around your model, so launch day is not the day you find out what was missed.',
      },
      {
        title: 'Customization beyond the console',
        body:
          'Code-level changes to the Web Template and the surrounding services when configuration is not enough, written so upgrades remain possible.',
      },
      {
        title: 'A senior team that owns the result',
        body:
          'Fixed-price milestones, clear communication and developers who have shipped marketplaces before, not a junior handed a ticket queue.',
      },
    ],

    extraSections: [
      {
        id: 'customizations',
        heading: 'What we customize on Sharetribe',
        intro:
          'Almost any part of a Sharetribe marketplace can be changed. These are the areas we are asked about most.',
        items: [
          {
            title: 'Design and Web Template',
            body:
              'Your brand across every page, custom layouts, new sections and components, mobile polish, and accessibility, built on the Sharetribe Web Template.',
          },
          {
            title: 'Transaction processes and listing types',
            body:
              'Bookings, purchases, enquiries and quotes, with the extra states, approvals, deposits and cancellation rules your business needs.',
          },
          {
            title: 'Payments, fees and payouts',
            body:
              'Stripe Connect onboarding, commission and fee rules, multiple currencies and payout behaviour, including the awkward cases like partial refunds.',
          },
          {
            title: 'Search, filters and extended data',
            body:
              'Custom listing fields, search schemas, filters, sorting and location search so people can find what the catalogue actually contains.',
          },
          {
            title: 'Integrations and automation',
            body:
              'Connecting Sharetribe to CRMs, helpdesks, email tools, analytics, video and scheduling services through its APIs, events and webhooks.',
          },
          {
            title: 'Admin tools and operations',
            body:
              'Moderation workflows, reporting, user and listing management, notifications and the back-office processes behind the marketplace.',
          },
        ],
      },
      {
        id: 'engagements',
        heading: 'Ways we work with Sharetribe projects',
        intro:
          'Some clients need a launch, some need a developer for a month, and some need someone to look after it. We are set up for all three.',
        items: [
          {
            title: 'New marketplace setup',
            body:
              'From scoping and configuration to design, build and launch, with a plan that separates what you need on day one from what can wait.',
          },
          {
            title: 'Customization and new features',
            body:
              'A defined piece of work, such as a new flow, a filter, an integration or a redesign, delivered against agreed milestones.',
          },
          {
            title: 'Audit, takeover and upgrades',
            body:
              'A review of an existing build, a tidy-up of the code, and updates to the current template and dependencies without breaking what works.',
          },
          {
            title: 'Migration to or from Sharetribe',
            body:
              'Moving a marketplace onto Sharetribe, or off it onto a custom build when you outgrow it, with listings, users and history carried across.',
          },
          {
            title: 'Ongoing support and development',
            body:
              'A dedicated developer or a support retainer for monitoring, fixes and the steady stream of improvements a live marketplace needs.',
          },
          {
            title: 'Technical consultation',
            body:
              'An honest second opinion on whether Sharetribe fits your model, what it will cost to customize and where the limits are before you commit.',
          },
        ],
      },
    ],

    challenges: [
      {
        title: 'A template that looks like everyone else',
        body:
          'The default experience works, but a marketplace needs its own identity and flows to earn trust and to be remembered by the people using it.',
      },
      {
        title: 'A transaction flow that almost fits',
        body:
          'Your model needs a step, approval or rule the standard process does not have, and workarounds in the interface are not a substitute for the right process.',
      },
      {
        title: 'Customizations that make upgrades painful',
        body:
          'Heavily edited templates fall behind the platform. The way a change is made decides whether the next update takes an hour or a week.',
      },
      {
        title: 'Integrations held together by hand',
        body:
          'Copying data between the marketplace, support and finance tools does not scale, and mistakes in it become customer problems.',
      },
      {
        title: 'Hitting the edge of the platform',
        body:
          'Sooner or later one requirement does not fit. Knowing whether to extend, work around or move on is a technical and a commercial decision.',
      },
    ],

    faqs: [
      {
        question: 'Can you build a marketplace on Sharetribe from scratch?',
        answer:
          'Yes. We handle the whole project: scoping what the first version needs, configuring Sharetribe, designing and building the customizations, setting up payments and emails, testing and launch. You can also hand us only the parts you do not want to do yourself.',
      },
      {
        question: 'What can be customized on a Sharetribe marketplace?',
        answer:
          'Nearly everything you see and a great deal that you do not: design and layout, listing types and extra fields, search and filters, transaction processes, fees and payouts, emails and notifications, and integrations with other tools. Configuration covers some of it and code covers the rest, and we will tell you which is which before we quote.',
      },
      {
        question: 'Do you work with the Sharetribe Web Template and the Sharetribe APIs?',
        answer:
          'Yes. We customize the Web Template, which is built on React and Node.js, and we work with the Marketplace and Integration APIs and with events and webhooks for anything that needs to happen outside the template.',
      },
      {
        question: 'I already have a Sharetribe marketplace. Can you take it over?',
        answer:
          'Yes. We begin with a short audit of the code, the configuration and the integrations so that we understand what is there and what is risky, and then agree a plan, whether that is fixes, new features or bringing the template up to date.',
      },
      {
        question: 'Can you help if my marketplace used Sharetribe Flex?',
        answer:
          'Yes. Sharetribe Flex is now simply called Sharetribe, and many marketplaces in the wild started under the older name. We work on both and can check what needs updating.',
      },
      {
        question: 'How much does Sharetribe customization cost?',
        answer:
          'It depends on scope, so we do not quote from a menu. After a short conversation about your model we propose a fixed price in milestones, so you know what you are paying before each stage starts. You also pay Sharetribe separately for the platform itself.',
      },
      {
        question: 'How long does it take to launch?',
        answer:
          'A well-scoped first version can often launch in a matter of weeks, and larger customizations take longer. The biggest factor is how clear the first version is, which is why we start by deciding what it must do on day one.',
      },
      {
        question: 'Is Sharetribe right for my marketplace?',
        answer:
          'Often, yes, especially if you want to launch quickly with a conventional listing and transaction model. If your matching, pricing or payout logic is the heart of the business, a custom build may serve you better, and we build those too. We will give you an honest recommendation.',
      },
      {
        question: 'Are you affiliated with Sharetribe?',
        answer:
          'No. We are an independent agency that builds on and around Sharetribe, and we are not endorsed by or part of the company. Sharetribe and its logo are trademarks of their respective owner, used here only to say which platform we work with.',
      },
    ],

    tags: [
      'Sharetribe marketplace setup',
      'Sharetribe Web Template customization',
      'Custom transaction processes',
      'Stripe Connect configuration',
      'Search & filters',
      'Integrations & APIs',
      'Upgrades & migrations',
      'Support & maintenance',
    ],

    caseStudyIds: ['dialux', 'truzeau'],
    reviewId: 'Rooted',
  },

  webDevelopment: {
    slug: 'web-development',
    metaTitle: 'Web Application Development',
    metaDescription:
      'Responsive, accessible web applications on React and Node, engineered to stay fast as the product, the content and the team all grow.',
    heading: 'Web development',
    intro:
      'We build web applications that stay quick and legible as they grow — on React and Node, with the performance and accessibility work treated as part of the build rather than a later pass.',
    body: [
      'Speed is a design constraint, not a cleanup task. That means budgets on what ships to the browser, images and fonts handled properly, and rendering chosen per page rather than by habit. This site is the example nearest to hand: every route is prerendered to static HTML with ReactDOMServer at build time and then hydrated, so a crawler and a first-time visitor both get a complete page without waiting for a bundle to execute.',
      'Accessibility gets the same treatment — real labels, keyboard paths, focus handling and contrast checked as the interface is built. It is cheaper there than in a retrofit, and the habits that make a page usable with a keyboard tend to make it clearer for everyone.',
    ],

    valueProps: [
      {
        title: 'Performance as a budget, not a cleanup',
        body:
          'Limits on what ships to the browser, images and fonts handled properly, and rendering chosen per page rather than by habit.',
      },
      {
        title: 'Accessible as it is built',
        body:
          'Real labels, keyboard paths, focus handling and contrast checked during the build, where they cost a fraction of a retrofit.',
      },
      {
        title: 'A design system rather than screens',
        body:
          'Shared components and tokens, so the tenth page costs less than the first and the product stays coherent as the team grows.',
      },
    ],

    challenges: [
      {
        title: 'Pages that get slower as the product succeeds',
        body:
          'Every feature adds script. Without a budget and a measurement in the pipeline, the slowdown is only noticed once it affects conversion.',
      },
      {
        title: 'A single-page app crawlers cannot read',
        body:
          'A client-rendered app serves search engines an empty div. Fixing it after launch means reworking rendering rather than adding a tag.',
      },
      {
        title: 'Accessibility discovered during procurement',
        body:
          'An audit demanded by a client or a regulator finds months of work. Built in from the start, most of it is simply how the markup was written.',
      },
    ],

    faqs: [
      {
        question: 'What stack do you build on?',
        answer:
          'React and Node by default, with TypeScript where a team will maintain it long term, and Postgres or MongoDB depending on the shape of the data. We are not religious about it: the constraint that matters more than the framework is whether your team can hire for and maintain what we leave behind.',
      },
      {
        question: 'How do you keep a site fast as it grows?',
        answer:
          'Budgets enforced in the build rather than measured afterwards, code split along real navigation boundaries, images served in modern formats at the size they are displayed, and fonts loaded without blocking text. Then a measurement in the pipeline, because performance work that is not re-checked regresses within a quarter.',
      },
      {
        question: 'Server rendering, static, or a single-page app?',
        answer:
          'Per page, by what the page is for. Marketing and content pages should arrive as HTML a crawler and a first-time visitor can read immediately; a logged-in dashboard behind authentication gains nothing from that and is simpler as a client-rendered app. This site prerenders every route to static HTML at build time and hydrates it, which is one working example of the first case.',
      },
      {
        question: 'Can you work with our existing codebase?',
        answer:
          'Yes, and it is a large part of what we do. That starts with reading it and writing down what we find — structure, risks, dependency state, what is untested — so the first conversation is about evidence rather than first impressions. Sometimes the honest recommendation is targeted repair rather than the rewrite that was expected.',
      },
    ],

    caseStudyIds: [],
  },

  mobileAppDevelopment: {
    slug: 'mobile-app-development',
    metaTitle: 'Mobile App Development',
    metaDescription:
      'Native-quality iOS and Android products from one React Native codebase, shipped through both stores and maintained after launch.',
    heading: 'Mobile app development',
    intro:
      'We build iOS and Android products from a single React Native codebase, dropping to native code where a feature genuinely needs it rather than as a matter of principle.',
    body: [
      "Mobile punishes assumptions the web forgives. A phone loses signal mid-task, sleeps in the middle of an upload, and belongs to someone who will not reopen the app if it lost their work. So the structural decisions come first: what happens offline, how state resynchronises, how notifications behave when the app is closed, and how payments respect each store's rules.",
      'Shipping is part of the work. Store listings, review feedback, signing and release pipelines, staged rollouts and crash reporting are all set up so that a fix can reach users in hours rather than at the mercy of a manual submission nobody has done before.',
    ],

    valueProps: [
      {
        title: 'One codebase, both stores',
        body:
          'React Native for the shared product, dropping to native code where a feature genuinely needs it rather than as a matter of principle.',
      },
      {
        title: 'Offline-first where it matters',
        body:
          'Explicit decisions about what works without signal, how state resynchronises and what happens to work in progress when the app is killed.',
      },
      {
        title: 'A release pipeline that is used',
        body:
          'Signing, staged rollouts, crash reporting and store metadata set up so a fix reaches users in hours rather than waiting on a manual submission nobody has done before.',
      },
    ],

    challenges: [
      {
        title: 'Work lost to a dropped connection',
        body:
          'Phones lose signal mid-task and sleep mid-upload. An app that loses what someone typed does not get a second chance from that user.',
      },
      {
        title: 'Store review as a surprise',
        body:
          'Payment rules, permission prompts, privacy declarations and account deletion requirements reject releases late. They are design constraints, not paperwork.',
      },
      {
        title: 'Notifications that arrive wrong or not at all',
        body:
          'Push behaviour differs by platform and by whether the app is closed. Getting it right is a backend and device concern as much as a frontend one.',
      },
    ],

    faqs: [
      {
        question: 'React Native or fully native?',
        answer:
          'React Native when the product is largely shared logic and screens, which covers most apps and roughly halves the build and the ongoing maintenance. Fully native when the app lives or dies on platform capability — heavy camera or audio processing, tight hardware integration, sustained high-frame-rate graphics. Plenty of apps are React Native with one or two native modules, and that is a fine answer.',
      },
      {
        question: 'How do store submissions and updates work?',
        answer:
          'We set up signing, build pipelines and staged rollouts, prepare the store listings and privacy declarations, and run the first submissions with you. After that a release is a pipeline run plus review time, and the rollout can be paused if crash reporting shows a problem.',
      },
      {
        question: 'Do we need an app at all, or would a web app do?',
        answer:
          'A responsive web app is cheaper to build and to change, and it is the right answer unless you need something the browser cannot give you: reliable push, offline use, background work, hardware access, or presence in the stores where your users look. We would rather have that conversation before the build than after it.',
      },
      {
        question: 'Can you take over an existing app?',
        answer:
          'Yes. The first step is an assessment — dependency and platform versions, what is tested, what is undocumented, what the release process assumes — because mobile punishes neglect quickly when operating systems move each year.',
      },
    ],

    caseStudyIds: [],
  },

  cloudSolutions: {
    slug: 'cloud-devops',
    metaTitle: 'Cloud & DevOps Services',
    metaDescription:
      'AWS infrastructure with CI/CD, observability and cost control built in — including the GPU and inference spend that AI features add.',
    heading: 'Cloud and DevOps',
    intro:
      'We set up infrastructure that a small team can operate confidently: reproducible environments, deployments that are boring to run, and enough visibility to answer "what changed?" without guesswork.',
    body: [
      'In practice that is infrastructure defined as code so an environment can be rebuilt rather than remembered, a pipeline that tests and ships on merge, logs and metrics that are actually queryable, and alerts that fire on things a person should wake up for and nothing else. The aim is a deploy that is unremarkable, because that is what makes frequent releases safe.',
      'Cost is part of the design. AI workloads in particular make it easy to spend badly — idle GPUs, oversized instances, a retrieval step calling a model it did not need to — so inference and storage spend get measured and attributed from the start rather than discovered on an invoice.',
    ],

    valueProps: [
      {
        title: 'Environments you rebuild, not remember',
        body:
          'Infrastructure defined as code, so a staging environment is reproducible and a recovery is a procedure rather than an archaeology exercise.',
      },
      {
        title: 'Deploys that are boring',
        body:
          'Test and ship on merge, with staged rollout and a rehearsed way back. Frequent releases are only safe when a release is unremarkable.',
      },
      {
        title: 'Spend you can attribute',
        body:
          'Cost measured per environment and per workload, including the GPU and inference spend AI features add, so a bill can be explained and acted on.',
      },
    ],

    challenges: [
      {
        title: 'Servers nobody dares touch',
        body:
          'A machine configured by hand years ago, with no record of how, is both a single point of failure and a blocker on every change around it.',
      },
      {
        title: 'Alert fatigue',
        body:
          'When everything pages, nothing does. Alerts have to correspond to things a person should act on, or they get muted and the real one is missed.',
      },
      {
        title: 'A cloud bill nobody can explain',
        body:
          'Idle instances, oversized databases and forgotten environments accumulate. Without tagging and attribution, cost reduction is guesswork.',
      },
    ],

    faqs: [
      {
        question: 'Which cloud do you work with?',
        answer:
          'AWS most often, and we work with whatever you are already on rather than proposing a migration for its own sake. Moving clouds is justified by a specific constraint — a managed service you need, a compliance requirement, a cost structure that does not work — not by preference.',
      },
      {
        question: 'How do you handle secrets and access?',
        answer:
          'Secrets in a managed secret store, never in the repository, with separate credentials per environment and no shared human logins to production. Access is scoped to roles, rotation is possible without a deploy, and what was granted to whom is recorded. This is also where most real incidents start, so it gets attention early.',
      },
      {
        question: 'What does observability actually include?',
        answer:
          'Structured logs you can query, metrics for the handful of numbers that describe health, traces where a request crosses services, and alerts tied to user-visible symptoms rather than machine statistics. The test is whether you can answer "what changed?" during an incident without guessing.',
      },
      {
        question: 'Can you reduce what we are spending now?',
        answer:
          'Usually, and the first step is attribution rather than cuts: tag resources, find what is idle, oversized or forgotten, and look at data transfer and storage tiers, which are where surprises hide. We would rather show you the breakdown and let you decide what is worth keeping than switch things off and find out.',
      },
    ],

    caseStudyIds: [],
  },

  uiUxDesign: {
    slug: 'ui-ux-design',
    metaTitle: 'UI/UX Design Services',
    metaDescription:
      'Research-led interface design that makes complex products feel obvious, including the interaction patterns AI features still need answers for.',
    heading: 'UI/UX design',
    intro:
      'We design interfaces for products with real complexity behind them — where the job is not decoration but making a complicated thing feel like it only has one obvious next step.',
    body: [
      'That starts with watching people attempt the actual task, which reliably contradicts the brief in useful ways. From there: structure before surface, a design system so the tenth screen costs less than the first, and prototypes tested while changing them is still cheap. Consistency is the point — a product where every screen is individually beautiful and collectively unfamiliar is harder to use than a plain one.',
      'AI features need patterns the last decade of interface convention does not supply. How to show that an answer is uncertain, where a person reviews and corrects, how to cite the source a claim came from, what a half-finished streaming response should look like. We design for those explicitly, because an AI feature people cannot calibrate their trust against gets abandoned however good the model is.',
    ],

    valueProps: [
      {
        title: 'Research that can change the plan',
        body:
          'Watching people attempt the real task, early enough that what we learn can still alter what gets built rather than merely explain it.',
      },
      {
        title: 'A system, not a pile of screens',
        body:
          'Components, tokens and patterns documented so the product stays coherent and the tenth screen is cheaper than the first.',
      },
      {
        title: 'Patterns for AI features',
        body:
          'Interfaces for uncertainty, review, correction and provenance — the parts of an AI feature that decide whether anyone trusts it.',
      },
    ],

    challenges: [
      {
        title: 'Every screen is beautiful and the product is confusing',
        body:
          'Screens designed one at a time drift apart. Consistency is what makes a complex product learnable, and it comes from a system.',
      },
      {
        title: 'Features nobody finds',
        body:
          'Work that was built, shipped and never discovered is a navigation and hierarchy problem, and it is cheaper to fix in a prototype.',
      },
      {
        title: 'An AI feature users cannot calibrate',
        body:
          'When people cannot tell how confident a system is or where an answer came from, they either over-trust it once or abandon it permanently.',
      },
    ],

    faqs: [
      {
        question: 'What does your research actually involve?',
        answer:
          'Talking to the people who will use the thing and watching them try the current version or a prototype — usually five to eight sessions, which is enough to find the serious problems. Plus whatever evidence already exists: support tickets, analytics, sales objections. The output is a short list of what to change, not a report nobody reads.',
      },
      {
        question: 'Do we get a design system we can keep using?',
        answer:
          'Yes, and it is built as the product is designed rather than as a separate exercise: tokens for colour, type and spacing, components with their states, and documentation of when to use which. It is handed over in Figma and implemented in code, so the two do not drift.',
      },
      {
        question: 'How do design and development overlap?',
        answer:
          'They run together. Design stays a step or two ahead rather than finishing first, developers review patterns while they are still cheap to change, and what is implemented gets checked against the design rather than assumed. A clean linear handover tends to produce screens that cannot be built as drawn.',
      },
      {
        question: 'How do you design for AI features specifically?',
        answer:
          'By treating uncertainty as a first-class state. That means showing where an answer came from, making the confidence legible, giving people an obvious way to correct or reject output, designing what a partial streaming response looks like, and deciding where a human must approve before anything happens. Conventional interface patterns do not cover these, so they are designed explicitly.',
      },
    ],

    caseStudyIds: [],
  },

  /* ---- Standalone: pages without a card in the landing grid ------------ */

  qualityAssurance: {
    slug: 'quality-assurance',
    title: 'Quality assurance & testing',
    description:
      'Automated and manual testing that catches regressions before release, wired into the pipeline rather than run as a phase.',
    tags: [
      'Test automation',
      'Manual & exploratory',
      'CI integration',
      'Release QA',
    ],
    metaTitle: 'QA & Software Testing Services',
    metaDescription:
      'Automated and manual software testing wired into your pipeline, so regressions are caught before release rather than reported by users.',
    heading: 'Quality assurance and testing',
    intro:
      'We build the testing a product needs to be changed safely: enough automated coverage to make a release boring, and enough human exploration to find what a script never would.',
    body: [
      'Coverage percentages are a poor goal, so we do not chase them. What matters is whether the tests fail when the product breaks, which means putting them where the risk actually is — the checkout, the permissions, the data migration — and running them on every merge rather than in a phase before a launch. A suite nobody trusts gets skipped, and a suite that takes an hour gets skipped too, so speed and reliability are part of the work.',
      'Alongside that, deliberate manual testing. Someone using the product as a person does, on a real device, finds the broken back button, the form that loses a long answer and the error message that explains nothing — none of which a passing assertion will ever tell you about. Both halves report into the same place, so a release decision is made on evidence rather than on nerve.',
    ],

    valueProps: [
      {
        title: 'Tests that fail when the product breaks',
        body:
          'Coverage aimed at real risk — payments, permissions, migrations — rather than at a percentage that looks good in a report.',
      },
      {
        title: 'Exploratory testing by people',
        body:
          'Someone using the product as a person does, on a real device, finding the broken back button and the error message that explains nothing.',
      },
      {
        title: 'Wired into the pipeline',
        body:
          'Running on every merge, fast enough that nobody is tempted to skip it, with failures that point at a cause rather than a screenshot.',
      },
    ],

    challenges: [
      {
        title: 'A suite nobody trusts',
        body:
          'Flaky tests get ignored, then disabled, then deleted. A suite that cries wolf is worse than none because it costs attention and buys nothing.',
      },
      {
        title: 'Regressions reported by customers',
        body:
          'When the first signal that something broke is a support ticket, the cost is not the bug — it is the confidence that took months to build.',
      },
      {
        title: 'Releases that need courage',
        body:
          'If shipping on a Friday is unthinkable, the problem is not the day. It is that nothing can tell you whether this release is safe.',
      },
    ],

    faqs: [
      {
        question: 'What do you automate first?',
        answer:
          'The paths where failure is expensive and the behaviour is stable: signup and login, checkout and payment, permissions, and anything touching money or personal data. Then the regressions you have actually had, because a bug that happened once tends to happen again. Fast unit and integration tests carry most of this; end-to-end tests cover the few journeys that must never break.',
      },
      {
        question: 'What coverage percentage should we aim for?',
        answer:
          'None in particular. Coverage measures which lines ran, not whether anything was verified, and chasing a number produces tests that assert nothing. The useful question is whether the suite fails when the product misbehaves — which we check by deliberately breaking things and seeing what goes red.',
      },
      {
        question: 'Do we still need manual testing?',
        answer:
          'Yes, but less of it and better aimed. Automation is for the things you want checked identically every time; people are for the things a script cannot notice — confusing copy, a layout that collapses on a real phone, a flow that technically works and makes no sense. Removing manual testing entirely tends to trade found bugs for unfound ones.',
      },
      {
        question: 'Can you test on real devices and browsers?',
        answer:
          'Yes — real iOS and Android hardware for the cases that matter, plus a device cloud for breadth, and the browsers your analytics say your users actually have rather than a list copied from somewhere else.',
      },
    ],

    caseStudyIds: [],
  },

  productManagement: {
    slug: 'product-management',
    title: 'Product management',
    description:
      'Discovery, prioritisation and delivery for teams that need a product owner rather than another pair of hands.',
    tags: ['Discovery', 'Roadmapping', 'Prioritisation', 'Delivery'],
    metaTitle: 'Product Management Services',
    metaDescription:
      'Embedded product management: discovery, prioritisation and delivery for teams who need someone owning what gets built and why.',
    heading: 'Product management',
    intro:
      'Sometimes the blocker is not engineering capacity but the absence of anyone whose job is deciding what to build next, and defending the decision when a louder request arrives.',
    body: [
      'The work starts with discovery that can change the plan: talking to the people who will use the thing, watching what they do today, and writing down what success would actually look like in numbers. Then a roadmap short enough to be honest — the next thing, the thing after it, and the reasons both are ahead of everything else. Shipped features nobody uses are more expensive than features never built, so the point of prioritisation is to find that out early.',
      'Day to day it is unglamorous: keeping scope legible, making trade-offs explicit rather than discovering them at the end, and writing enough down that a stakeholder can disagree with a decision instead of being surprised by it. We do this embedded in your team, and the aim is to leave a way of working behind rather than a dependency on us.',
    ],

    valueProps: [
      {
        title: 'Discovery that can change the plan',
        body:
          'Talking to users and watching what they do today, early enough that the findings can still alter the roadmap rather than justify it.',
      },
      {
        title: 'A roadmap short enough to be honest',
        body:
          'The next thing, the thing after it, and the reason both are ahead of everything else — instead of a twelve-month list nobody believes.',
      },
      {
        title: 'Decisions written down',
        body:
          'Trade-offs made explicit as they happen, so a stakeholder can disagree with a decision rather than be surprised by its consequences.',
      },
    ],

    challenges: [
      {
        title: 'Everything is priority one',
        body:
          'When every request is urgent, sequencing happens by whoever asked most recently, and the team is busy without moving anything forward.',
      },
      {
        title: 'Shipped and unused',
        body:
          'Features built without evidence of demand are more expensive than features never built, because they also have to be maintained.',
      },
      {
        title: 'Scope discovered at the end',
        body:
          'Work that looked small grows during delivery when the edge cases were never written down, and the deadline absorbs the difference.',
      },
    ],

    faqs: [
      {
        question: 'What does an embedded product manager actually do?',
        answer:
          'Decides what gets built next and defends the decision: running discovery, writing down what success looks like in numbers, keeping scope legible, and making sure engineers are never guessing what a thing is for. Day to day it looks like conversations, written decisions and a short roadmap — not a Gantt chart.',
      },
      {
        question: 'How do you prioritise between competing requests?',
        answer:
          'Against a stated goal, with the cost of each option made visible. Most disagreements about priority turn out to be disagreements about the goal, which is why the first job is usually writing the goal down. Where evidence is thin, we prefer the cheapest experiment that would change our mind over a longer argument.',
      },
      {
        question: 'How is success measured?',
        answer:
          'By a number agreed before the work starts — activation, retention, conversion, time saved — and by whether we can attribute movement to the change. If nobody can name the number a feature is meant to move, that is the finding, and it is better found before the build.',
      },
      {
        question: 'How long is an engagement, and what are we left with?',
        answer:
          'It varies with the gap being filled: a few weeks of discovery to unblock a decision, or an ongoing part-time product owner alongside your team. Either way the aim is to leave a way of working behind — a written goal, a prioritisation anyone can apply, a roadmap your team maintains — rather than a dependency on us.',
      },
    ],

    caseStudyIds: [],
  },

  softwareMaintenance: {
    slug: 'software-maintenance',
    title: 'Software maintenance & support',
    description:
      'Keeping an existing system secure, current and fixable — including one somebody else wrote and left.',
    tags: ['Dependency updates', 'Bug fixing', 'Monitoring', 'Legacy handover'],
    metaTitle: 'Software Maintenance & Support',
    metaDescription:
      'Ongoing maintenance for an existing system: security and dependency updates, bug fixes, monitoring, and taking over code somebody else wrote.',
    heading: 'Software maintenance and support',
    intro:
      'Most software spends far longer being maintained than being built, and that is usually the part nobody planned for. We take on systems already in production, including ones we did not write.',
    body: [
      'That means the things that quietly decide whether a product is still viable in three years: dependency and security updates applied before they become a forced migration, bugs fixed at the cause rather than patched at the symptom, monitoring that notices a failure before a customer reports it, and a deployment the current team can actually run. Deferred maintenance is a debt with compounding interest — a framework two major versions behind is not a chore, it is a project.',
      'Taking over unfamiliar code starts with reading it and writing down what we find: what the system does, where the risks are, what is undocumented, and what would happen if the one person who understands it left. That assessment is useful on its own, and it is the honest basis for deciding whether to maintain, refactor or replace — a decision we would rather you make with evidence than with optimism.',
    ],

    valueProps: [
      {
        title: 'Updates before they become migrations',
        body:
          'Security patches and dependency upgrades applied steadily, so you are never two major versions behind and facing a project instead of a chore.',
      },
      {
        title: 'Fixes at the cause',
        body:
          'Bugs traced to why they happened, with a test that keeps them fixed, rather than patched at the symptom and met again next quarter.',
      },
      {
        title: 'Monitoring that notices first',
        body:
          'Errors and outages surfaced by alerting rather than by a customer, with enough context to diagnose without reproducing by hand.',
      },
    ],

    challenges: [
      {
        title: 'Two major versions behind',
        body:
          'Deferred upgrades compound. What would have been an afternoon each quarter becomes a migration that competes with a roadmap.',
      },
      {
        title: 'A bus factor of one',
        body:
          'When a single person understands the system and nothing is written down, every change waits on their availability and their memory.',
      },
      {
        title: 'No idea whether it is healthy',
        body:
          'Without monitoring, an outage lasts as long as it takes a customer to complain, and recurring errors stay invisible indefinitely.',
      },
    ],

    faqs: [
      {
        question: 'Can you take over a system you did not build?',
        answer:
          'Yes, and that is most of this work. It starts with reading the code and writing down what we find: what the system does, how it is deployed, where the risks are, what is untested and what is undocumented. That assessment is useful on its own, whoever maintains the system afterwards.',
      },
      {
        question: 'What does the initial assessment cover?',
        answer:
          'Dependency and runtime versions against their support dates, known vulnerabilities, test coverage where it matters, how a release is made, what monitoring exists, where the data lives and how it is backed up, and the single points of failure — including the human ones. You get a written summary with risks ordered by what they would cost.',
      },
      {
        question: 'How is maintenance work arranged?',
        answer:
          'Usually as a recurring block of time per month covering updates, fixes and small improvements, with anything larger quoted separately so a roadmap item never quietly consumes the maintenance budget. We would rather agree what is in and out of scope up front than discover it during an incident.',
      },
      {
        question: 'Should we maintain this or rewrite it?',
        answer:
          'Maintain, far more often than people expect. A rewrite restarts the clock on every bug the current system has already found, and it competes with the roadmap for a year. The honest cases for rewriting are a platform that is genuinely unsupported, or a core model so wrong that every change fights it — and that is a judgement we would rather make after the assessment than before.',
      },
    ],

    caseStudyIds: [],
  },
};

/** Every page id, in the order pages should be listed. */
export const allServicePageIds = Object.keys(configServicePages);

/**
 * The pages that appear in lists: the hub, related services, the homepage
 * list, navigation. Hidden pages are excluded here and nowhere else, so a
 * hidden page cannot leak into a list by accident. Routes, prerendering and
 * the sitemap use `allServicePageIds`.
 */
export const servicePageIds = allServicePageIds.filter(id => !configServicePages[id].hidden);

/**
 * One service page's content, whichever kind of entry it is.
 *
 * Consumers should use this rather than reading the two configs themselves:
 * a grid service takes its title, description and tags from configServices, a
 * standalone page carries its own, and nothing outside this file should have
 * to know which it is looking at.
 *
 * @returns {object|null} null when no page exists for the id
 */
export const resolveServicePage = id => {
  const page = configServicePages[id];
  if (!page) return null;

  const service = configServices.find(s => s.id === id) || null;

  return {
    id,
    ...page,
    title: page.title || service?.title,
    description: page.description || service?.description,
    tags: page.tags || service?.tags || [],
    artId: page.artId || id,
    isAi: Boolean(service?.isAi),
    /* True when this service also has a card in the landing-page grid. */
    inLandingGrid: Boolean(service),
  };
};

/** Route name for a service page, e.g. 'ServicePage_aiAgents'. */
export const serviceRouteName = id => `ServicePage_${id}`;

/** Path for a service page, e.g. '/services/ai-agent-development'. */
export const servicePagePath = id => `/services/${configServicePages[id].slug}`;

export default configServicePages;
